import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, forkJoin, map } from 'rxjs';

import { GbeEndPointV4 } from '../../models/gbe/gbe-endpoint.enum';
import { GbeUserV4, toUserModel } from '../../models/gbe/gbe-user.model';
import {
  GaliaPermission,
  GaliaPermissions,
  UserModel,
  UserPermissionName
} from '../../models/user.model';
import { GBE_BASE_URL } from '../../tokens/gbe-url.token';

interface GaliaPermissionsResponse {
  permissions: GaliaPermission[];
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly gbeUrl = inject(GBE_BASE_URL);

  /** @returns User profile enriched with the business unit's Galia permissions. */
  public getUserProfile$(): Observable<UserModel> {
    return forkJoin({
      profile: this.http.get<GbeUserV4>(this.buildUrl(GbeEndPointV4.PROFILE)),
      permissions: this.getPermissions$(),
      userPermissions: this.getMyPermissions$()
    }).pipe(
      map(({ profile, permissions, userPermissions }) => ({
        ...toUserModel(profile),
        permissions,
        userPermissions
      }))
    );
  }

  updateProfile$(data: { firstName?: string; lastName?: string }): Observable<void> {
    const url = this.buildUrl(GbeEndPointV4.PROFILE);
    return this.http.patch<void>(url, data);
  }

  updateLanguage$(locale: string): Observable<void> {
    const url = this.buildUrl(GbeEndPointV4.PROFILE_LANGUAGE);
    return this.http.patch<void>(url, { locale });
  }

  /** @returns the caller's global user-level permissions (e.g. ['manage_api_keys']). */
  getMyPermissions$(): Observable<UserPermissionName[]> {
    const url = this.buildUrl(GbeEndPointV4.USER_PERMISSIONS);
    return this.http
      .get<{ permissions: UserPermissionName[] }>(url)
      .pipe(map((res) => res.permissions ?? []));
  }

  /** @returns the caller's own business unit's Galia permissions, keyed by name. */
  public getPermissions$(): Observable<GaliaPermissions> {
    const url = this.buildUrl(GbeEndPointV4.BUSINESS_UNITS_GALIA_PERMISSIONS);
    return this.http
      .get<GaliaPermissionsResponse>(url)
      .pipe(
        map((res) =>
          Object.fromEntries((res.permissions ?? []).map((p) => [p.name, p.value]))
        )
      );
  }

  private buildUrl(endpoint: string): string {
    return `${this.gbeUrl}/${endpoint}`;
  }
}
